import { Logger } from './logs'
import { getEnvVar } from './api/metadata'

const REQUEST_TIMEOUT_MS = 30_000 // 30 seconds
export const KEEPALIVE_PING_INTERVAL_SEC = 50 // 50 seconds

export const KEEPALIVE_PING_HEADER = 'Keepalive-Ping-Interval'

/**
 * Connection options for requests to the API.
 */
export interface ConnectionOpts {
  /**
   * E2B API key to use for authentication.
   *
   * @default AS_API_KEY // environment variable
   */
  apiKey?: string
  /**
   * E2B access token to use for authentication.
   *
   * @default AS_ACCESS_TOKEN // environment variable
   */
  accessToken?: string
  /**
   * Domain to use for the API.
   *
   * @default AS_DOMAIN // environment variable or `e2b.app`
   */
  domain?: string
  /**
   * If true the SDK starts in the debug mode and connects to the local envd API server.
   * @internal
   * @default AS_DEBUG // environment variable or `false`
   */
  debug?: boolean
  /**
   * Timeout for requests to the API in **milliseconds**.
   *
   * @default 30_000 // 30 seconds
   */
  requestTimeoutMs?: number
  /**
   * Logger to use for logging messages. It can accept any object that implements `Logger` interface—for example, {@link console}.
   */
  logger?: Logger

  /**
   * Additional headers to send with the request.
   */
  headers?: Record<string, string>
}

/**
 * Configuration for connecting to the API.
 */
// 导出ConnectionConfig类
export class ConnectionConfig {
  // 是否开启调试模式
  readonly debug: boolean
  // 域名
  readonly domain: string
  // API地址
  readonly apiUrl: string
  // 日志记录器
  readonly logger?: Logger

  // 请求超时时间
  readonly requestTimeoutMs: number

  // API密钥
  readonly apiKey?: string
  // 访问令牌
  readonly accessToken?: string

  // 请求头
  readonly headers?: Record<string, string>

  // 构造函数，用于初始化ConnectionConfig对象
  constructor(opts?: ConnectionOpts) {
    // 如果opts中有apiKey，则使用opts中的apiKey，否则使用ConnectionConfig中的apiKey
    this.apiKey = opts?.apiKey || ConnectionConfig.apiKey
    // 如果opts中有debug，则使用opts中的debug，否则使用ConnectionConfig中的debug
    this.debug = opts?.debug || ConnectionConfig.debug
    // 如果opts中有domain，则使用opts中的domain，否则使用ConnectionConfig中的domain
    this.domain = opts?.domain || ConnectionConfig.domain
    // 如果opts中有accessToken，则使用opts中的accessToken，否则使用ConnectionConfig中的accessToken
    this.accessToken = opts?.accessToken || ConnectionConfig.accessToken
    // 如果opts中有requestTimeoutMs，则使用opts中的requestTimeoutMs，否则使用REQUEST_TIMEOUT_MS
    this.requestTimeoutMs = opts?.requestTimeoutMs ?? REQUEST_TIMEOUT_MS
    // 如果opts中有logger，则使用opts中的logger
    this.logger = opts?.logger
    // 如果opts中有headers，则使用opts中的headers
    this.headers = opts?.headers

    // 如果debug为true，则apiUrl为http://localhost:3000，否则为https://api.domain
    this.apiUrl = this.debug
      ? 'http://localhost:3000'
      : `https://api.${this.domain}`
  }

  // 获取域名
  private static get domain() {
    // 从环境变量中获取AS_DOMAIN，如果没有则使用e2b.app
    return getEnvVar('AS_DOMAIN') || 'agentsphere.run'
  }

  // 获取调试模式
  private static get debug() {
    // 从环境变量中获取AS_DEBUG，如果没有则使用false，并将其转换为小写，如果为true则返回true，否则返回false
    return (getEnvVar('AS_DEBUG') || 'false').toLowerCase() === 'true'
  }

  // 获取API密钥
  private static get apiKey() {
    // 从环境变量中获取AS_API_KEY
    return getEnvVar('AS_API_KEY')
  }

  // 获取访问令牌
  private static get accessToken() {
    // 从环境变量中获取AS_ACCESS_TOKEN
    return getEnvVar('AS_ACCESS_TOKEN')
  }

  // 获取信号
  getSignal(requestTimeoutMs?: number) {
    // 如果requestTimeoutMs有值，则使用requestTimeoutMs，否则使用requestTimeoutMs
    const timeout = requestTimeoutMs ?? this.requestTimeoutMs

    // 如果timeout有值，则返回AbortSignal.timeout(timeout)，否则返回undefined
    return timeout ? AbortSignal.timeout(timeout) : undefined
  }
}

/**
 * User used for the operation in the sandbox.
 */
export type Username = 'root' | 'user'

export const defaultUsername: Username = 'user'
